import { Button } from '@/components/ui/button';
import { sanitizeHTML } from '@/lib/sanitize-html';
import { ImageIcon } from 'lucide-react';

interface WelcomePreviewProps {
  coverImageUrl: string | null;
  greetingTitle: string;
  welcomeMessage: string;
  /** When provided, the "Get Started" button is active and calls this (public runtime). */
  onStart?: () => void;
}

function resolveUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
}

// Inline approximation of the external welcome page the candidate will see.
// {{tokens}} render literally here — they're resolved at send time (HW-580).
export function WelcomePreview({
  coverImageUrl,
  greetingTitle,
  welcomeMessage,
  onStart,
}: WelcomePreviewProps) {
  const resolvedUrl = resolveUrl(coverImageUrl);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {resolvedUrl ? (
        <img src={resolvedUrl} alt="" className="aspect-video w-full object-cover" />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-hygge/10 to-muted">
          <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
        </div>
      )}
      <div className="space-y-3 p-6 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          {greetingTitle || 'Welcome to the team!'}
        </h2>
        {welcomeMessage ? (
          <div
            className="prose prose-sm dark:prose-invert mx-auto max-w-none text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(welcomeMessage) }}
          />
        ) : (
          <p className="text-sm text-muted-foreground/60">Your welcome message will appear here.</p>
        )}
        <div className="pt-2">
          <Button
            onClick={onStart}
            disabled={!onStart}
            className={onStart ? undefined : 'pointer-events-none'}
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
}
